import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  ArrowForward,
  Business,
  CheckCircle,
  CloudSync,
  Flag,
  HomeOutlined,
  Payments,
  Public,
  Sensors,
  Shield,
  Speed,
  Visibility,
  WaterDrop,
} from '@mui/icons-material';
import Navbar from './Navbar';
import Footer from './Footer';
import happy from './Assets/happy.jpeg';
import { Helmet } from 'react-helmet-async';

const MotionDiv = motion.div;

const Main = styled.div`
  min-height: 100vh;
  background: #f6f9fc;
  color: #0f172a;
`;

const Container = styled.main`
  padding-top: 76px;
`;

const Hero = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background:
    radial-gradient(circle at 82% 18%, rgba(14, 165, 233, 0.34), transparent 34%),
    linear-gradient(135deg, #06111f 0%, #0b1f3a 45%, #075985 100%);
  color: white;
`;

const HeroInner = styled.div`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 86px 0 64px;
`;

const Eyebrow = styled.div`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border: 1px solid rgba(186, 230, 253, 0.38);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #bae6fd;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const Heading = styled.h1`
  max-width: 780px;
  margin: 20px 0 18px;
  font-size: clamp(2.4rem, 5.4vw, 4.2rem);
  line-height: 1.06;
  font-weight: 800;
  letter-spacing: 0;
`;

const HeroText = styled.p`
  max-width: 680px;
  margin: 0;
  color: #dbeafe;
  font-size: clamp(1.02rem, 1.8vw, 1.2rem);
  line-height: 1.8;
`;

const HeroHighlights = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 36px;
`;

const HighlightChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  font-size: 0.92rem;
  font-weight: 600;

  svg {
    color: #38bdf8;
    font-size: 1.2rem;
  }
`;

const sectionVariants = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.52, ease: 'easeOut' } },
};

const Section = styled(motion.section)`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 80px 0;

  @media (max-width: 720px) {
    padding: 56px 0;
  }
`;

function AnimatedSection({ children, ...props }) {
  return (
    <Section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      {...props}
    >
      {children}
    </Section>
  );
}

const SectionHeader = styled.div`
  max-width: 760px;
  margin-bottom: 34px;

  ${({ centered }) => centered && `
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  `}
`;

const Kicker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2563eb;
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 12px 0 0;
  color: #0f172a;
  font-size: clamp(1.8rem, 3.2vw, 2.6rem);
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: 0;
`;

const Text = styled.p`
  margin: 16px 0 0;
  color: #475569;
  font-size: 1.02rem;
  line-height: 1.8;
`;

const SplitSection = styled(Section)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 0.9fr);
  align-items: center;
  gap: clamp(32px, 6vw, 78px);

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const StoryText = styled.div`
  p {
    margin: 0 0 16px;
    color: #475569;
    font-size: 1.02rem;
    line-height: 1.8;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const ImagePanel = styled.div`
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: white;
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.1);

  img {
    display: block;
    width: 100%;
    height: 420px;
    object-fit: cover;
  }

  @media (max-width: 560px) {
    img {
      height: 260px;
    }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 940px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.article`
  padding: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: white;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.055);
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(37, 99, 235, 0.32);
    box-shadow: 0 24px 55px rgba(37, 99, 235, 0.11);
  }

  h3 {
    margin: 18px 0 10px;
    color: #0f172a;
    font-size: 1.08rem;
    line-height: 1.35;
  }

  p {
    margin: 0;
    color: #64748b;
    line-height: 1.68;
  }
`;

const IconBox = styled.div`
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: #eff6ff;
  color: #2563eb;
`;

const Checklist = styled.ul`
  display: grid;
  gap: 14px;
  padding: 0;
  margin: 28px 0 0;
  list-style: none;
`;

const ChecklistItem = styled.li`
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: start;
  gap: 12px;
  color: #334155;
  font-size: 1.02rem;
  line-height: 1.7;

  svg {
    margin-top: 3px;
    color: #0ea5e9;
    font-size: 1.1rem;
  }
`;

const AltSection = styled(Section)`
  padding-top: 0;
`;

const VisionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const VisionCard = styled.div`
  padding: 30px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);

  h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 12px;
    color: white;
    font-size: 1.2rem;
    font-weight: 800;
  }

  svg {
    color: #38bdf8;
  }

  p {
    margin: 0;
    color: #dbeafe;
    line-height: 1.8;
  }
`;

const VisionSection = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: 28px;
  background:
    radial-gradient(circle at 90% 20%, rgba(125, 211, 252, 0.22), transparent 32%),
    linear-gradient(135deg, #071428, #0f3a5f);
  color: white;
  padding: clamp(34px, 6vw, 58px);
`;

const FinalCta = styled.section`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto 80px;
  padding: clamp(34px, 6vw, 58px);
  border-radius: 28px;
  background:
    radial-gradient(circle at 90% 20%, rgba(125, 211, 252, 0.22), transparent 32%),
    linear-gradient(135deg, #071428, #0f3a5f);
  color: white;
  text-align: center;

  h2 {
    max-width: 760px;
    margin: 0 auto;
    font-size: clamp(1.9rem, 4vw, 2.8rem);
    line-height: 1.12;
    letter-spacing: 0;
  }

  p {
    max-width: 640px;
    margin: 18px auto 28px;
    color: #dbeafe;
    line-height: 1.8;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 14px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 48px;
  padding: 0 22px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: #2563eb;
  color: white;
  font-size: 0.96rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease;
  box-shadow: 0 18px 35px rgba(37, 99, 235, 0.28);

  &:hover {
    background: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 22px 45px rgba(37, 99, 235, 0.34);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.22);
  color: white;
  box-shadow: none;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(255, 255, 255, 0.36);
    box-shadow: none;
  }
`;

const services = [
  {
    icon: <Sensors />,
    title: 'Ultrasonic Smart Water Meters',
    text: 'Precision metering hardware built for accurate, long-term consumption tracking.',
  },
  {
    icon: <CloudSync />,
    title: 'Mobile Telemetry Systems',
    text: 'Real-time data collection and monitoring delivered straight to the cloud.',
  },
  {
    icon: <HomeOutlined />,
    title: 'Remote Meter Reading & Control',
    text: 'Read, manage, and act on meter data from anywhere, at any time.',
  },
  {
    icon: <Payments />,
    title: 'Automated Billing & Prepaid Water',
    text: 'Streamlined billing workflows paired with flexible prepaid solutions.',
  },
  {
    icon: <Speed />,
    title: 'Leak Detection & Analytics',
    text: 'AI-powered insights that catch losses early and reduce non-revenue water.',
  },
];

const sectors = [
  {
    icon: <Business />,
    title: 'Public Water Utilities',
    text: 'Advanced smart metering for efficient municipal resource management.',
  },
  {
    icon: <WaterDrop />,
    title: 'Non-Revenue Water Management',
    text: 'Detecting and eliminating water loss across distribution networks.',
  },
  {
    icon: <Business />,
    title: 'Commercial & Industrial',
    text: 'Smart metering solutions for factories, hotels, and businesses.',
  },
  {
    icon: <HomeOutlined />,
    title: 'Residential & Last Mile',
    text: 'Smart metering for households, apartments, and gated communities.',
  },
  {
    icon: <Public />,
    title: 'Sustainable Water Conservation',
    text: 'Supporting global efforts in water sustainability and stewardship.',
  },
];

const whyChooseUs = [
  'Tech-driven innovation across every product we build',
  'Environmental responsibility built into our mission',
  'Enterprise-grade solutions ready for scale',
  'Deployable across every sector, from homes to utilities',
  'Data-driven, secure, and built for reliability',
];

const About = () => {
  return (
    <Main>
      <Helmet>
        <title>About AquaTools</title>
        <meta
          name="description"
          content="Learn more about AquaTools, a leader in smart water metering solutions leveraging IoT and cloud technology."
        />
        <meta name="keywords" content="smart water meters, NB-IoT, AquaTools, water conservation" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Navbar />

      <Container>
        <Hero>
          <HeroInner>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
            >
              <Eyebrow>
                <WaterDrop fontSize="small" />
                About AquaTools
              </Eyebrow>
              <Heading>Empowering Smart Water Management</Heading>
              <HeroText>
                We're passionate about creating sustainable solutions for one of the planet's most vital
                resources — water. Our mission is to bridge technology and environmental responsibility
                by providing intelligent metering and monitoring tools that help individuals, businesses,
                and governments manage consumption more efficiently.
              </HeroText>
              <HeroHighlights>
                <HighlightChip>
                  <Sensors fontSize="small" />
                  NB-IoT Smart Metering
                </HighlightChip>
                <HighlightChip>
                  <CloudSync fontSize="small" />
                  Cloud-Connected Analytics
                </HighlightChip>
                <HighlightChip>
                  <Shield fontSize="small" />
                  Secure & Reliable
                </HighlightChip>
              </HeroHighlights>
            </MotionDiv>
          </HeroInner>
        </Hero>

        <SplitSection
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div>
            <Kicker>
              <WaterDrop fontSize="small" />
              Our Story
            </Kicker>
            <Title>Built on innovation, driven by precision</Title>
            <StoryText>
              <p>
                Founded on innovation and built with precision, AquaTools is more than just a tech
                platform — it's a movement toward smarter, data-driven decision-making.
              </p>
              <p>
                Our integrated systems deliver real-time insights, reduce waste, and put the power of
                conservation directly in your hands, helping teams manage water consumption more
                efficiently every day.
              </p>
            </StoryText>
          </div>
          <ImagePanel>
            <img src={happy} alt="AquaTools smart water conservation" />
          </ImagePanel>
        </SplitSection>

        <AltSection>
          <SectionHeader centered>
            <Kicker>
              <Sensors fontSize="small" />
              What we offer
            </Kicker>
            <Title>Our Services</Title>
            <Text>
              AquaTools provides a full suite of smart water metering solutions tailored for efficiency,
              accuracy, and seamless integration.
            </Text>
          </SectionHeader>

          <FeatureGrid>
            {services.map(({ icon, title, text }) => (
              <FeatureCard key={title}>
                <IconBox>{icon}</IconBox>
                <h3>{title}</h3>
                <p>{text}</p>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </AltSection>

        <SplitSection
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div>
            <Kicker>
              <Shield fontSize="small" />
              Why Choose Us
            </Kicker>
            <Title>Technology that solves real-world problems</Title>
            <Text>
              At AquaTools, we believe that technology can solve real-world problems. We're here to help
              you take control of your water usage — and build a more sustainable tomorrow.
            </Text>
            <Checklist>
              {whyChooseUs.map((item) => (
                <ChecklistItem key={item}>
                  <CheckCircle fontSize="small" />
                  <span>{item}</span>
                </ChecklistItem>
              ))}
            </Checklist>
          </div>
          <div>
            <FeatureGrid style={{ gridTemplateColumns: '1fr' }}>
              <FeatureCard>
                <IconBox>
                  <Public />
                </IconBox>
                <h3>Serving every water sector</h3>
                <p>
                  From public utilities to residential estates, our solutions scale to fit the way each
                  sector manages water.
                </p>
              </FeatureCard>
            </FeatureGrid>
          </div>
        </SplitSection>

        <AltSection>
          <SectionHeader centered>
            <Kicker>
              <Public fontSize="small" />
              Who we serve
            </Kicker>
            <Title>AquaTools Core Sectors</Title>
            <Text>
              AquaTools serves a wide range of industries, providing smart water metering solutions
              across every stage of the water network.
            </Text>
          </SectionHeader>

          <FeatureGrid>
            {sectors.map(({ icon, title, text }) => (
              <FeatureCard key={title}>
                <IconBox>{icon}</IconBox>
                <h3>{title}</h3>
                <p>{text}</p>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </AltSection>

        <AnimatedSection>
          <VisionSection>
            <VisionGrid>
              <VisionCard>
                <h3>
                  <Visibility fontSize="small" />
                  Our Vision
                </h3>
                <p>
                  To lead the world toward a future where every drop of water is measured, valued, and
                  preserved.
                </p>
              </VisionCard>
              <VisionCard>
                <h3>
                  <Flag fontSize="small" />
                  Our Mission
                </h3>
                <p>
                  To provide smart, scalable water management solutions that promote conservation,
                  reduce cost, and empower communities to take control of their resources.
                </p>
              </VisionCard>
            </VisionGrid>
          </VisionSection>
        </AnimatedSection>

        <FinalCta>
          <h2>Ready to modernize water management with AquaTools?</h2>
          <p>
            Explore our smart meters or get in touch — we'll help you find the right metering and
            monitoring solution for your site.
          </p>
          <HeroActions>
            <StyledLink to="/contactus">
              <Button>
                Talk to us
                <ArrowForward fontSize="small" />
              </Button>
            </StyledLink>
            <StyledLink to="/products">
              <SecondaryButton>View Products</SecondaryButton>
            </StyledLink>
          </HeroActions>
        </FinalCta>
      </Container>

      <Footer />
    </Main>
  );
};

export default About;
